import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../store/store";
import { FaCircleUser } from "react-icons/fa6";
import { getCountryValueDisplay } from "../../features/country/countrySlice";
import styles from "./ProfileInfo.module.css";

const UserIcon = FaCircleUser as unknown as React.ComponentType<any>;

const capitalizeFirstLetter = (value: string) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const ProfileInfoPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);

  const userId = user?.id || user?._id || "";
  const statusValue = String(user?.status || "").toLowerCase();
  const roleValue = String(user?.role || "user").toLowerCase();
  const canAccessDashboard = ["admin", "moderator"].includes(roleValue);
  const locale = (i18n.resolvedLanguage || "fr").split("-")[0];

  const statusLabel = useMemo(() => {
    if (statusValue === "single" || statusValue === "célibataire" || statusValue === "celibataire") {
      return t("signup.statusSingle");
    }
    if (statusValue === "married" || statusValue === "marié" || statusValue === "marie_sans_enfants") {
      return t("signup.statusMarriedNoChildren");
    }
    if (
      statusValue === "married_with_children" ||
      statusValue === "marié avec enfants" ||
      statusValue === "marie_avec_enfants"
    ) {
      return t("signup.statusMarriedWithChildren");
    }
    return t("profileInfo.unknown");
  }, [statusValue, t]);

  const roleLabel = useMemo(() => {
    if (roleValue === "admin") return t("profileInfo.roleAdmin");
    if (roleValue === "moderator") return t("profileInfo.roleModerator");
    return t("profileInfo.roleUser");
  }, [roleValue, t]);

  const displayName = useMemo(() => {
    const lastName = capitalizeFirstLetter(String(user?.lastName || ""));
    const firstName = capitalizeFirstLetter(String(user?.firstName || ""));
    return `${lastName} ${firstName}`.trim();
  }, [user?.firstName, user?.lastName]);

  if (!user) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <p className={styles.state}>{t("profileInfo.loginRequired")}</p>
          <Link to="/register" className={styles.ctaSecondary}>
            {t("header.signup")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.shell}>
        <header className={styles.header}>
          <div>
            <h1>{t("profileInfo.title")}</h1>
            <p>{t("profileInfo.subtitle")}</p>
          </div>
          {userId && (
            <Link to={`/profile/${userId}`} className={styles.ctaPrimary}>
              {t("profileInfo.editProfile")}
            </Link>
          )}
        </header>

        <div className={styles.identityCard}>
          <div className={styles.avatarIconWrap} aria-hidden="true">
            <UserIcon className={styles.avatarIcon} />
          </div>
          <div>
            <h2>{displayName || t("profileInfo.unknown")}</h2>
            <p>{roleLabel}</p>
          </div>
        </div>

        <div className={styles.grid}>
          <article className={styles.card}>
            <h3>{t("profileInfo.contactTitle")}</h3>
            <p><strong>{t("signup.email")}:</strong> {user?.email || t("profileInfo.unknown")}</p>
            <p>
              <strong>{t("signup.phone")}:</strong>{" "}
              {`${user?.indicatif || ""} ${user?.phone || ""}`.trim() || t("profileInfo.unknown")}
            </p>
          </article>

          <article className={styles.card}>
            <h3>{t("profileInfo.personalTitle")}</h3>
            <p><strong>{t("signup.status")}:</strong> {statusLabel}</p>
            <p><strong>{t("signup.nbrChild")}:</strong> {user?.nbr_child ?? t("profileInfo.unknown")}</p>
            <p><strong>{t("myAds.userCountry")}:</strong> {getCountryValueDisplay(user?.country, user?.codeCountry || t("profileInfo.unknown"), locale)}</p>
          </article>

          <article className={styles.card}>
            <h3>{t("profileInfo.accountTitle")}</h3>
            <p><strong>{t("profileInfo.roleLabel")}:</strong> {roleLabel}</p>
            <p><strong>{t("profileInfo.userId")}:</strong> {String(userId || t("profileInfo.unknown"))}</p>
            {canAccessDashboard && (
              <Link to="/dashboard" className={styles.dashboardLink}>
                {t("profileInfo.dashboardLink")}
              </Link>
            )}
          </article>
        </div>
      </section>
    </div>
  );
};

export default ProfileInfoPage;
